import { createSelector } from "@reduxjs/toolkit";
import type { Action } from "redux";

import { getState } from "../helpers";

const REQUEST_STATUS_PENDING = "pending";
const REQUEST_STATUS_FULFILLED = "fulfilled";
const REQUEST_STATUS_REJECTED = "rejected";

const statuses = [
  REQUEST_STATUS_PENDING,
  REQUEST_STATUS_FULFILLED,
  REQUEST_STATUS_REJECTED,
].join("|");

export function getRequestMatches(type: string) {
  const regex = new RegExp(`(.*)/(${statuses})`);
  return regex.exec(type);
}

const reducer = (state = {}, action: Action) => {
  const { type } = action;
  const matches = getRequestMatches(type);

  // Ignore if the request is not of type `pending`, `fulfilled` or `rejected`.
  if (!matches) return state;

  const [, requestName, requestState] = matches;
  return {
    ...state,
    // true when receiving `example/fetch/pending`
    // false when receiving `example/fetch/fulfilled` or `example/fetch/rejected`
    [requestName]: {
      isLoading: requestState === REQUEST_STATUS_PENDING,
      isFirstLoading: state[requestName] === undefined ? true : false,
    },
  };
};

const getRequestType = createSelector(
  (_, params) => params,
  (params) => params.typePrefix
);

/**
 * `getIsLoading` returns true any time a request with the specified key is loading.
 */
const getIsLoading = createSelector(
  getState,
  getRequestType,
  (state, requestType) => {
    const request = state.requests && state.requests[requestType];

    if (request === undefined) {
      return true;
    }

    return request.isLoading;
  }
) as (state, fn: any) => boolean;

/**
 * `getIsFirstLoading` returns true only only on the first request with this key.
 */
const getIsFirstLoading = createSelector(
  getState,
  getRequestType,
  (state, requestType) => {
    const request = state.requests && state.requests[requestType];

    if (request === undefined) {
      return true;
    }

    return request.isFirstLoading;
  }
) as (state, fn: any) => boolean;

export { getIsLoading, getIsFirstLoading };

export default reducer;
